import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import classnames from 'classnames'
import React, { useCallback, useRef, useState } from 'react'
import type { UploadOptions } from '@paintscout/util/s3'
import { PERMITTED_FILE_TYPES } from '@paintscout/util/s3'
import Button from '@paintscout/ui/components/Button'
import Dropzone from '@paintscout/ui/components/Dropzone'
import FilePreview from '@paintscout/ui/components/FilePreview'
import InputLabel from '@paintscout/ui/components/InputLabel'
import Spinner from '@paintscout/ui/components/Spinner'
import type { StyleClasses } from '@paintscout/ui/styles'
import { useTranslation } from '@paintscout/ui'

import PhotoCropDialog from '@paintscout/ui/components/PhotoCropDialog'
import type { PhotoCropResult } from '@paintscout/ui/components/PhotoCropper'
import type { FocalCircle } from '@paintscout/util/util'
import { cropAreaToFocalCircle, focalCircleToCropArea } from '@paintscout/util/util'
import { useImageUpload } from './useImageUpload'
import FocalCropPreview from './FocalCropPreview'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  label: {
    marginBottom: theme.typography.pxToRem(5)
  },
  dropzone: {
    position: 'relative',
    flexGrow: 1,
    maxHeight: '150px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.borderRadius.md,
    '& $button': {
      // material-ui will spit out a warning about not being able to find $button, but it still works
      // https://github.com/mui-org/material-ui/issues/15511
      minHeight: (props: Partial<UploadImageProps>) => (props.buttonHeight ? props.buttonHeight : 0)
    }
  },
  // the round crop preview is narrower than a photo preview; keep it at the left like the buttons
  focalDropzone: {
    justifyContent: 'flex-start'
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(-2),
    gap: theme.spacing(1)
  },
  actionButton: {
    backgroundColor: 'transparent !important'
  },
  cropCaption: {
    marginBottom: theme.spacing(1)
  },
  button: {
    height: '100%'
  },
  above: {
    display: 'flex',
    alignItems: 'space-between'
  },
  clearButton: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(-2),
    backgroundColor: 'transparent !important'
  },
  acceptDrop: {},
  rejectDrop: {
    '& $button': {
      borderColor: theme.paintscout.palette.brand.Neutral.P85
    }
  },
  previewOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    background: 'rgba(255,255,255,0.7)'
  },
  preview: {
    width: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  img: {
    '&:not($disabled)': {
      cursor: 'pointer'
    },
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  avatar: {
    borderRadius: '50%',
    '& img': {
      maxHeight: '100%',
      maxWidth: '100%',
      borderRadius: '50%',
      objectFit: 'contain !important' as any,
      backgroundColor: '#FFFFFF'
    }
  },
  productImage: {
    maxHeight: '140px',
    maxWidth: '120px',
    // Grow the fixed-height filePreview box to the 140px cap, so tall images
    // (paint cans) get the full height instead of the 110px default (#25029).
    '&$filePreview': {
      height: '140px'
    },
    // Fit the img inside that box. Capping the img at 140px while the box was
    // 110px let tall images overflow and get clipped by overflow: hidden.
    '& img': {
      maxHeight: '100%',
      maxWidth: '100%'
    }
  },
  disabled: {},
  filePreview: {
    border: `1px dashed transparent`,
    height: '110px',
    '&:hover': {
      cursor: (props: Partial<UploadImageProps>) => (!props.disabled ? 'pointer' : 'default')
    }
  },
  activeOutline: {
    borderColor: `${theme.palette.primary.main}`
  },
  rejectOutline: {
    borderColor: `${theme.palette.error.main}`
  }
}))

export interface UploadImageProps {
  classes?: StyleClasses<typeof useStyles>
  uploadOptions?: UploadOptions
  /**
   * use cloudinaryPublicId or s3PublicKey if possible,
   * but need to pass to make src for resizable image(s3 images)
   */
  src: string
  // Desired image sources
  cloudinaryPublicId?: string
  s3PublicKey?: string
  label?: string
  className?: string
  disabled?: boolean
  disabledNote?: string
  disableClear?: boolean
  showUploadButton?: boolean
  format?: string
  pages?: number
  accept?: string | string[]
  maxWidth?: number
  maxHeight?: number
  buttonHeight?: number
  quality?: number
  avatar?: boolean
  productImage?: boolean
  omitSizes?: string[]
  onFileSelected?: (file: File) => void
  onUpload: (args: {
    src: string
    cloudinaryPublicId: string
    s3PublicKey: string
    format: string
    pages: number
    width: number
    height: number
    type: string
  }) => any
  onClear: () => any
  /**
   * Set (with `onFocalPointChange`) to add a Crop button that opens the photo in a round cropper:
   * drag the photo to put a face in the circle and zoom with the slider.
   */
  focalPoint?: Partial<FocalCircle> | null
  onFocalPointChange?: (focalPoint: FocalCircle) => void
  /** Adds a Crop button beside Clear that calls this, for a caller with its own crop dialog */
  onCrop?: () => void
}

function UploadImage({
  className,
  src,
  cloudinaryPublicId,
  s3PublicKey,
  format,
  pages,
  label,
  disabled,
  disabledNote,
  disableClear,
  onClear,
  onUpload,
  maxWidth,
  maxHeight,
  buttonHeight,
  accept = PERMITTED_FILE_TYPES,
  uploadOptions,
  avatar = false,
  productImage = false,
  showUploadButton = false,
  omitSizes,
  onFileSelected,
  focalPoint,
  onFocalPointChange,
  onCrop,
  ...props
}: UploadImageProps) {
  const { __ } = useTranslation()
  const classes = useStyles({ buttonHeight, disabled, ...props })
  const fileInputRef = useRef<HTMLInputElement>(null)

  const publicId = cloudinaryPublicId || s3PublicKey
  const [focalCropOpen, setFocalCropOpen] = useState(false)
  const canCrop = !!src && (!!onCrop || !!onFocalPointChange)
  // a saved crop shows the photo as presentations do; with only a centre saved, show it whole
  const focalCrop =
    onFocalPointChange && src && focalPoint?.size
      ? { x: focalPoint.x ?? 50, y: focalPoint.y ?? 50, size: focalPoint.size }
      : null

  const getFocalArea = useCallback(
    (imageAspect: number) => (focalPoint ? focalCircleToCropArea({ x: 50, y: 50, ...focalPoint }, imageAspect) : null),
    [focalPoint]
  )

  const handleFocalCrop = ({ area, imageAspect }: PhotoCropResult) => {
    setFocalCropOpen(false)
    onFocalPointChange?.(cropAreaToFocalCircle(area, imageAspect))
  }

  const handleCropClick = () => {
    if (onCrop) onCrop()
    else setFocalCropOpen(true)
  }
  const { loading, handleDrop } = useImageUpload({
    maxWidth,
    maxHeight,
    uploadOptions,
    onUpload,
    onFileSelected,
    errorMessage: __('Unable to upload image')
  })

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <div
      {...props}
      className={classnames(
        {
          [classes.root]: true,
          [classes.disabled]: !!disabled
        },
        className
      )}
    >
      <div className={classes.above}>
        {label && (
          <InputLabel shrink={true} className={classes.label}>
            {label}
          </InputLabel>
        )}
      </div>
      <Dropzone
        className={classnames(classes.dropzone, { [classes.focalDropzone]: !!focalCrop }, 'tour-upload-image-dropzone')}
        onDrop={handleDrop}
        disabled={loading || disabled}
        text={disabledNote ?? ''}
        accept={accept}
        buttonFullHeight={true}
        shouldRenderPreview={loading || !!publicId || !!src}
        renderPreview={({ isDragActive, draggedFiles, isDragReject }) => {
          const rejectDrag = isDragReject && draggedFiles.filter((file) => file.type !== '').length > 0
          return (
            <div className={classes.preview}>
              {loading ? (
                <div className={classes.previewOverlay}>
                  <Spinner fullWidth={true} fullHeight={true} />
                </div>
              ) : focalCrop ? (
                <FocalCropPreview src={src} focalPoint={focalCrop} />
              ) : (
                <FilePreview
                  className={classnames({
                    [classes.filePreview]: true,
                    [classes.activeOutline]: isDragActive && !rejectDrag,
                    [classes.rejectOutline]: rejectDrag,
                    [classes.avatar]: avatar,
                    [classes.productImage]: productImage
                  })}
                  file={{ src, s3PublicKey, cloudinaryPublicId, type: 'image', format, pages, visibility: 'visible' }}
                  omitSizes={omitSizes}
                />
              )}
            </div>
          )
        }}
      />
      {!loading && !disabled && canCrop && (
        <div className={classes.actions}>
          <Button className={classes.actionButton} variant="text" onClick={handleCropClick}>
            {__('Crop')}
          </Button>
          {!disableClear && (
            <Button className={classes.actionButton} variant="text" onClick={onClear}>
              {__('Clear')}
            </Button>
          )}
        </div>
      )}
      {!loading && !disabled && !canCrop && !disableClear && (
        <Button className={classes.clearButton} variant="text" disabled={!src && !publicId} onClick={onClear}>
          {__('Clear')}
        </Button>
      )}
      {onFocalPointChange && (
        <PhotoCropDialog
          open={focalCropOpen}
          image={src}
          initialArea={getFocalArea}
          caption={
            <Typography variant="caption" color="textSecondary" component="p" className={classes.cropCaption}>
              {__('Drag the photo and zoom so your face fills the circle. Presentations show what is inside it.')}
            </Typography>
          }
          onConfirm={handleFocalCrop}
          onCancel={() => setFocalCropOpen(false)}
        />
      )}
      {!loading && !disabled && showUploadButton && (
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={(e) => {
            const files = e.target.files
            if (files && files.length > 0) {
              handleDrop(Array.from(files))
            }
          }}
        />
      )}
      {!loading && !disabled && showUploadButton && (
        <div
          className={classnames({
            [classes.clearButton]: true
          })}
        >
          <Button className={classes.clearButton} variant="outlined" onClick={handleButtonClick}>
            {__('+ Upload')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default UploadImage
